import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faBolt,
  faBox,
  faBoxesStacked,
  faChevronDown,
  faGlasses,
  faLayerGroup,
  faTag,
  faTruckFast,
  faTriangleExclamation,
  faWind,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import { Footer, LogoLockup, WA_NUMBER } from '../components/SiteChrome'
import { BrandEasyOrder } from '../components/BrandEasyOrder'
import { BrandTrustSections, type TrustQuestion } from '../components/BrandTrustSections'
import { LpCrossLinks } from '../components/LpCrossLinks'
import { COMET_MASK_DEFINITIONS } from '../data/cometMasks'
import type { CometMaskDefinition } from '../data/cometMasks'
import { whatsAppCtaProps, trackSelectItem } from '../lib/analytics'
import {
  getProductPrimaryImage,
  getStorefrontConfig,
  listStorefrontCategories,
  listStorefrontProducts,
} from '../lib/dropify'
import type { StorefrontCategory, StorefrontConfig, StorefrontProduct } from '../lib/dropify'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildBrandLandingSchema, buildBreadcrumbSchema, buildFaqSchema } from '../lib/seo'
import { getBrandProofImages } from '../lib/testimonials'
import type { FaqItem } from '../lib/seo'

const PAGE_PATH = '/masker-comet'
const PAGE_CATEGORY = 'Masker COMET'

type CometMaskProduct = CometMaskDefinition & { image: string }

const CHOICE_GUIDE = [
  {
    icon: faBox,
    title: 'Theo 3 Ply',
    desc: 'Isi 50 pcs untuk pemakaian operasional harian dengan pilihan earloop atau headloop.',
    target: 'comet-theo-3-ply-earloop-box-50-pcs',
  },
  {
    icon: faGlasses,
    title: 'Theo 4D',
    desc: 'Bentuk 4D memberi ruang di depan mulut dan membantu mengurangi embun pada kacamata.',
    target: 'comet-theo-4d-box-20-pcs',
  },
  {
    icon: faLayerGroup,
    title: 'Theo KN95',
    desc: 'Konstruksi 5 ply dan BFE 98%, tersedia dalam box isi 20 pcs.',
    target: 'comet-theo-kn95-box-20-pcs',
  },
  {
    icon: faWind,
    title: 'Theo 4 Ply Carbon',
    desc: 'Menggunakan lapisan karbon aktif dan ditujukan untuk area kerja berdebu serta berbau.',
    target: 'comet-theo-4-ply-carbon-box-50-pcs',
  },
]

const DROPIFY_REASONS = [
  {
    icon: faBolt,
    title: 'Pilih tipe masker via WhatsApp',
    desc: 'Kirim tipe, jumlah box, warna, dan kota tujuan. Admin akan memeriksa stok serta menyiapkan quotation.',
  },
  {
    icon: faBoxesStacked,
    title: 'Box isi 20 atau 50 pcs',
    desc: 'Theo 3 Ply dan 4 Ply Carbon berisi 50 pcs per box. Theo 4D dan KN95 berisi 20 pcs. Pilih berdasarkan kebutuhan operasional.',
  },
  {
    icon: faTag,
    title: 'Data BFE, CE, ISO, AKD per tipe',
    desc: 'Setiap tipe memiliki data produk yang berbeda. Admin memeriksa dokumen yang tersedia sebelum tipe dicantumkan dalam PO.',
  },
  {
    icon: faTruckFast,
    title: 'Kirim ke kantor, pabrik, klinik, atau gudang',
    desc: 'Pesanan dapat dikirim ke berbagai lokasi di seluruh Indonesia dengan ekspedisi yang disesuaikan.',
  },
]

const COMET_MASK_TRUST_QUESTIONS: TrustQuestion[] = [
  {
    q: 'Apakah Dropify menjual masker dan sarung tangan COMET?',
    a: 'Ya. Halaman ini membahas masker COMET Theo; sarung tangan kerja COMET punya halaman spesifikasi sendiri. Keduanya bisa digabung dalam satu pesanan.',
  },
  {
    q: 'Bagaimana cara memilih tipe masker COMET yang tepat?',
    a: 'Pilih dari kondisi pemakaian dan dokumen yang diwajibkan. Theo 3 Ply untuk pemakaian harian, 4D untuk ruang mulut lebih besar, KN95 untuk BFE 98%, dan 4 Ply Carbon untuk area berdebu atau berbau.',
  },
  {
    q: 'Pengadaan kantor kami butuh jumlah besar tiap bulan. Bagaimana mengaturnya?',
    a: 'Sebutkan tipe masker, jumlah box per periode, dan kota tujuan. Admin mengecek stok tiap kali jadwal pengadaan Anda tiba dan menyiapkan quotation baru.',
  },
  {
    q: 'Bisakah pesan beberapa tipe masker dalam satu kiriman?',
    a: 'Bisa. Gabungkan misalnya Theo 3 Ply untuk pemakaian harian dan KN95 untuk area tertentu dalam satu pesanan. Admin menghitung total box dan ongkirnya sekaligus.',
  },
  {
    q: 'Berapa lama pengiriman masker COMET dari Surabaya?',
    a: 'Kiriman masker berangkat dari Surabaya. Admin memeriksa waktu penyiapan setelah produk, jumlah, dan pembayaran dikonfirmasi. Estimasi perjalanan mengikuti kota tujuan dan layanan ekspedisi.',
  },
]

export const COMET_MASK_FAQ: FaqItem[] = [
  {
    q: 'Apa perbedaan masker COMET Theo 3 Ply, 4D, KN95, dan Carbon?',
    a: 'Theo 3 Ply berisi 50 pcs per box dan biasa dipilih untuk pemakaian harian. Theo 4D berisi 20 pcs dengan bentuk yang memberi ruang di depan mulut. Theo KN95 berisi 20 pcs, memakai konstruksi 5 ply, dan memiliki data BFE 98%. Theo 4 Ply Carbon berisi 50 pcs serta memakai lapisan karbon aktif. Pilih tipe berdasarkan kondisi pemakaian dan dokumen yang diwajibkan di tempat kerja Anda.',
  },
  {
    q: 'Berapa isi setiap box masker COMET?',
    a: 'Theo 3 Ply dan Theo 4 Ply Carbon berisi 50 pcs per box. Theo 4D dan Theo KN95 berisi 20 pcs per box. Untuk menghitung jumlah box, gunakan jumlah pemakai, frekuensi ganti masker, dan lama periode pengadaan.',
  },
  {
    q: 'Apakah semua masker COMET memiliki nomor AKD?',
    a: 'Nomor AKD tersedia pada dokumen Theo 3 Ply, Theo 4D, dan Theo KN95. Data untuk Theo 4 Ply Carbon belum dapat kami pastikan dari dokumen yang tersedia. Jika nomor AKD menjadi syarat pengadaan, sebutkan tipe yang dipilih dan minta dokumennya kepada admin sebelum membuat PO.',
  },
  {
    q: 'Apakah BFE 98% sama dengan perlindungan terhadap seluruh partikel atau gas?',
    a: 'Tidak. BFE 98% menjelaskan hasil uji efisiensi filtrasi bakteri dengan metode tertentu. Angka tersebut tidak menyatakan perlindungan terhadap semua aerosol, debu berbahaya, gas, atau uap kimia. Tim K3 tetap perlu memilih masker atau alat pelindung pernapasan berdasarkan bahaya di area kerja.',
  },
  {
    q: 'Apakah warna masker COMET bisa dipilih?',
    a: 'Bisa, selama warnanya tersedia. Theo 3 Ply memiliki pilihan hijau, hitam, dan putih. Theo 4D serta KN95 memiliki pilihan hitam, putih, abu-abu, dan krem. Minta foto stok aktual agar warna yang dipilih sesuai dengan barang yang akan dikirim.',
  },
  {
    q: 'Bagaimana cara meminta harga, stok, dan dokumen?',
    a: 'Kirim tipe masker, jumlah box, pilihan warna, kota tujuan, dan dokumen yang diwajibkan melalui WhatsApp. Admin akan mengecek stok, menghitung harga serta ongkir, lalu mengirim dokumen produk yang tersedia.',
  },
]

const COMET_MASK_SCHEMA = COMET_MASK_DEFINITIONS.map((product, index) => ({
  '@type': 'ListItem',
  position: index + 1,
  item: {
    '@type': 'Thing',
    name: product.name,
    sku: product.sku,
    description: `${product.layers}, ${product.pack}. ${product.material}. ${product.standard}.`,
    image: product.image,
    url: absoluteUrl(`/produk/${product.slug}`),
    brand: { '@type': 'Brand', name: 'COMET' },
  },
}))

function resolveMaskProducts(products: StorefrontProduct[]): CometMaskProduct[] {
  return COMET_MASK_DEFINITIONS.map((definition) => {
    const product = products.find((item) => item.sku === definition.sku || item.slug === definition.slug)
    return {
      ...definition,
      slug: product?.slug || definition.slug,
      image: product
        ? getProductPrimaryImage(product, { width: 760, format: 'webp', quality: 76 })
        : definition.image,
    }
  })
}

export const Route = createFileRoute('/masker-comet')({
  loader: async () => {
    const [config, categories, response, proofImages] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      listStorefrontProducts({ search: 'masker COMET', per_page: 20 }).catch(() => null),
      getBrandProofImages(),
    ])

    return { config, categories, products: resolveMaskProducts(response?.items ?? []), proofImages }
  },
  head: () => {
    const title = brandTitle('Masker COMET Theo 3 Ply, 4D, KN95 & Carbon')
    const description =
      'Masker COMET Theo 3 Ply, 4D, KN95 & Carbon per box. BFE, CE, ISO, AKD per tipe. Cek stok, harga, dokumen & kirim se-Indonesia via WhatsApp.'
    const heroImage = COMET_MASK_DEFINITIONS.find((item) => item.sku === 'COMET-THEO-KN95')?.image
      ?? COMET_MASK_DEFINITIONS[0].image

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(PAGE_PATH) },
        { property: 'og:locale', content: 'id_ID' },
        { property: 'og:image', content: heroImage },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: heroImage },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PAGE_PATH) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBrandLandingSchema({ brandName: 'COMET', pageName: title, description, path: PAGE_PATH })),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: 'Beranda', path: '/' },
              { name: 'Merek', path: '/merek' },
              { name: 'Masker COMET', path: PAGE_PATH },
            ]),
          ),
        },
        { type: 'application/ld+json', children: JSON.stringify(buildFaqSchema([...COMET_MASK_FAQ, ...COMET_MASK_TRUST_QUESTIONS])) },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Produk Masker COMET di Dropify',
            itemListElement: COMET_MASK_SCHEMA,
          }),
        },
      ],
    }
  },
  component: MaskerCometRoute,
})

function MaskerCometRoute() {
  const { config, categories, products, proofImages } = Route.useLoaderData()
  return <MaskerCometLandingPage config={config} categories={categories} products={products} proofImages={proofImages} />
}

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

function WhatsAppButton({
  message,
  label,
  placement,
  itemName,
  className,
}: {
  message: string
  label: string
  placement: string
  itemName?: string
  className: string
}) {
  return (
    <a
      href={waLink(message)}
      target="_blank"
      rel="noopener noreferrer"
      {...whatsAppCtaProps('landing_page', {
          cta_placement: placement,
          lp_category: PAGE_CATEGORY,
          ...(itemName
            ? {
                lead_item_name: itemName,
                items: [{ item_id: itemName, item_name: itemName, item_category: PAGE_CATEGORY }],
              }
            : {}),
        })}
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 flex-shrink-0" />
      {label}
    </a>
  )
}

function SectionLabel({ children }: { children: string }) {
  return <p className="text-xs font-black uppercase tracking-[0.22em] text-sky-700">{children}</p>
}

export function MaskerCometLandingPage({
  config = null,
  categories = [],
  products = resolveMaskProducts([]),
  proofImages = [],
}: {
  config?: StorefrontConfig | null
  categories?: StorefrontCategory[]
  products?: CometMaskProduct[]
  proofImages?: string[]
}) {
  const heroMessage =
    'Halo Dropify, saya mencari masker COMET Theo. Tipe: ___. Jumlah: ___ box. Warna: ___. Kota tujuan: ___. Dokumen wajib: ___. Bisa cek stok dan quotation?'

  return (
    <div className="min-h-screen bg-[#f5fbff] text-slate-950">
      <div className="bg-sky-300 px-4 py-2 text-center text-xs font-black text-[#08243a] sm:text-sm">
        4 tipe masker COMET · Box isi 20/50 pcs · Sample & dokumen tersedia
      </div>

      <header className="border-b border-white/10 bg-[#08243a] text-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <a href="/" aria-label="Kembali ke Dropify"><LogoLockup size="sm" invert /></a>
          <WhatsAppButton
            message={heroMessage}
            label="Periksa stok masker COMET"
            placement="header"
            className="inline-flex items-center gap-2 rounded-full border border-sky-300/60 px-4 py-2.5 text-sm font-bold text-sky-200 transition-colors hover:bg-sky-300 hover:text-[#08243a]"
          />
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-[#08243a] text-white">
          <div aria-hidden className="absolute -right-40 -top-40 h-[36rem] w-[36rem] rounded-full bg-sky-400/10 blur-2xl" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 md:py-20 lg:grid-cols-[0.92fr_1.08fr]">
            <div>
              <p className="inline-flex rounded-full bg-sky-300 px-4 py-2 text-xs font-black uppercase tracking-wider text-[#08243a]">COMET Theo Mask Series</p>
              <h1 className="mt-6 max-w-3xl text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
                Masker COMET Theo untuk <span className="text-sky-300">pemakaian harian dan kebutuhan perusahaan.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
                Bandingkan Theo 3 Ply, 4D, KN95, dan 4 Ply Carbon berdasarkan jumlah lapisan, isi box, pilihan warna, data BFE, serta dokumen produk yang tersedia.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <WhatsAppButton
                  message={heroMessage}
                  label="Cek stok & minta quotation"
                  placement="hero_primary"
                  className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-green-700 px-6 py-4 text-base font-black text-white shadow-lg shadow-black/20 transition-colors hover:bg-green-800"
                />
                <a href="#produk-masker-comet" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-6 py-4 text-sm font-black transition-colors hover:bg-white/10">
                  Bandingkan empat tipe
                  <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {products.map((product, index) => (
                <article
                  key={product.sku}
                  data-hero-product-visual
                  className={`overflow-hidden rounded-3xl border border-white/10 bg-white p-3 shadow-xl ${index % 2 === 1 ? 'translate-y-5' : '-translate-y-1'}`}
                >
                  <img src={product.image} alt={product.name} width="480" height="480" fetchPriority={index < 2 ? 'high' : undefined} decoding="async" className="aspect-square w-full object-contain" />
                  <div className="px-2 pb-2 pt-3 text-[#08243a]">
                    <span className="text-sm font-black">{product.shortName}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-12 md:py-16" aria-labelledby="tentang-masker-comet">
          <div className="mx-auto max-w-4xl px-4">
            <h2 id="tentang-masker-comet" className="text-2xl font-black tracking-tight md:text-3xl">Mengenal masker COMET Theo</h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-slate-600">
              <p>COMET Theo memiliki beberapa jenis masker, termasuk 3 Ply, 4D, KN95, dan 4 Ply Carbon. Bentuk dan lapisannya berbeda: masker 4D memiliki ruang di depan mulut, sedangkan tipe Carbon memakai lapisan karbon aktif.</p>
              <p>Theo 3 Ply dapat digunakan untuk pemakaian harian dan tersedia dalam pilihan earloop atau headloop. Theo KN95 memiliki lima lapisan dan dikemas 20 pcs per box; Theo 4 Ply Carbon dikemas 50 pcs per box.</p>
            </div>
          </div>
        </section>

        <BrandEasyOrder />

        <section className="border-b border-slate-200 bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.75fr_1.25fr] lg:gap-16">
            <div>
              <SectionLabel>Mulai dari kebutuhan</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Empat bentuk, isi box, dan data yang berbeda</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {CHOICE_GUIDE.map((item) => (
                <a key={item.title} href={`#${item.target}`} className="group rounded-3xl border border-slate-200 bg-[#f5fbff] p-5 transition-all hover:border-sky-300 hover:shadow-lg">
                  <FontAwesomeIcon icon={item.icon} className="h-6 w-6 text-sky-700" />
                  <h3 className="mt-5 text-xl font-black">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-black text-sky-700">Lihat detail <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section id="produk-masker-comet" className="scroll-mt-6 bg-[#f5fbff] py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="max-w-3xl">
              <SectionLabel>Produk yang tersedia</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Bandingkan COMET Theo per tipe</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">Gunakan kode model ketika meminta stok agar tipe, warna, isi box, dan dokumen tidak tertukar.</p>
            </div>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {products.map((product) => (
                <article id={product.slug.replace(/^masker-/, '')} key={product.sku} className="group flex scroll-mt-6 flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:border-sky-300 hover:shadow-xl">
                  <a
                    href={`/produk/${product.slug}`}
                    aria-label={`Lihat detail ${product.name}`}
                    onClick={() => trackSelectItem({ item_id: product.sku, item_name: product.name, item_category: PAGE_CATEGORY }, 'Masker COMET brand landing products')}
                    className="relative block overflow-hidden bg-white"
                  >
                    <img src={product.image} alt={product.name} width="560" height="560" loading="lazy" decoding="async" className="aspect-square w-full object-contain p-4 transition-transform duration-500 group-hover:scale-105" />
                    <span className="absolute left-4 top-4 rounded-full bg-[#08243a] px-3 py-1 text-[11px] font-black text-sky-300">{product.sku}</span>
                  </a>
                  <div className="flex flex-1 flex-col p-5">
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-sky-700">{product.layers} · {product.pack}</p>
                    <h3 className="mt-2 text-xl font-black tracking-tight">
                      <a href={`/produk/${product.slug}`} className="hover:text-sky-700">{product.shortName}</a>
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{product.feature}</p>
                    <div className="mt-4 space-y-2 rounded-2xl bg-slate-50 p-4 text-xs leading-relaxed text-slate-600">
                      <p><strong className="text-slate-900">Bahan:</strong> {product.material}</p>
                      <p><strong className="text-slate-900">Warna:</strong> {product.colors}</p>
                      <p><strong className="text-slate-900">Data:</strong> {[product.standard, product.bfe, product.akd].filter(Boolean).join(' · ')}</p>
                    </div>
                    <WhatsAppButton
                      message={`Halo Dropify, saya mau cek masker COMET ${product.shortName} (${product.sku}). Kebutuhan: ___ box. Warna: ___. Kota tujuan: ___. Bisa kirim stok, harga, dan dokumennya?`}
                      label={`Tanya ${product.shortName}`}
                      placement="product_card"
                      itemName={product.name}
                      className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 px-4 py-3 text-sm font-black text-white transition-colors hover:bg-green-800"
                    />
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 lg:grid-cols-2">
            <div className="rounded-[2rem] border border-slate-200 p-6 md:p-8">
              <SectionLabel>BFE dalam konteks</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight">BFE membantu membandingkan data katalog</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">BFE adalah hasil uji efisiensi filtrasi bakteri. Theo 3 Ply dan 4D mencantumkan BFE 95%, sedangkan Theo KN95 mencantumkan BFE 98%.</p>
              <div className="mt-6 rounded-2xl bg-sky-50 p-5 text-sm leading-relaxed text-sky-950">
                Nilai BFE tidak menjelaskan seluruh bahaya pernapasan. Debu berbahaya, aerosol, gas, uap kimia, kekurangan oksigen, dan paparan khusus membutuhkan evaluasi alat pelindung pernapasan yang sesuai.
              </div>
            </div>
            <aside className="rounded-[2rem] border border-amber-200 bg-amber-50 p-6 md:p-8">
              <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-amber-700"><FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4" />Batas penggunaan</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight text-amber-950">Periksa bahaya sebelum menetapkan tipe</h2>
              <div className="mt-6 space-y-3 text-sm leading-relaxed text-amber-950">
                {[
                  'Paparan gas, uap kimia, atau atmosfer dengan kekurangan oksigen.',
                  'Debu atau aerosol berbahaya dengan kewajiban respirator tertentu.',
                  'Pekerjaan yang mewajibkan fit test atau sistem perlindungan pernapasan perusahaan.',
                  'Kebutuhan medis atau prosedur klinis dengan standar khusus di luar data katalog.',
                ].map((item) => (
                  <div key={item} className="flex gap-3 rounded-2xl border border-amber-200 bg-white/80 p-4"><FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />{item}</div>
                ))}
              </div>
            </aside>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <div>
              <SectionLabel>Data pemesanan</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Tipe, isi box, dokumen, stok, dan pengiriman diperiksa per SKU</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">Kirim tipe masker, jumlah box, kota tujuan, dan dokumen yang diwajibkan. Admin akan memeriksa SKU yang sesuai sebelum menyiapkan quotation.</p>
              <WhatsAppButton message={heroMessage} label="Chat admin Masker COMET" placement="why_dropify" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-green-700 px-5 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {DROPIFY_REASONS.map((item) => (
                <article key={item.title} className="rounded-2xl border border-slate-200 bg-[#f5fbff] p-5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#08243a] text-sky-300"><FontAwesomeIcon icon={item.icon} className="h-5 w-5" /></div>
                  <h3 className="mt-4 text-lg font-black tracking-tight">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <div className="text-center"><SectionLabel>FAQ Masker COMET</SectionLabel><h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Pertanyaan tentang masker COMET</h2></div>
            <div className="mt-9 divide-y divide-slate-200 border-y border-slate-200">
              {COMET_MASK_FAQ.map((item) => (
                <details key={item.q} className="group py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-left font-black text-slate-900">{item.q}<FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 flex-shrink-0 text-sky-700 transition-transform group-open:rotate-180" /></summary>
                  <p className="mt-3 max-w-3xl pr-8 text-sm leading-relaxed text-slate-600 md:text-base">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-sky-300 py-14 text-[#08243a]">
          <div className="mx-auto flex max-w-7xl flex-col justify-between gap-7 px-4 lg:flex-row lg:items-center">
            <div className="max-w-3xl">
              <p className="text-xs font-black uppercase tracking-[0.2em]">Siap cek kebutuhan Anda</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Kirim tipe, jumlah box, warna, dan kota tujuan.</h2>
              <p className="mt-3 text-sm leading-relaxed text-sky-950 md:text-base">Sertakan BFE, AKD, atau dokumen lain yang diwajibkan agar pengecekan pertama lebih lengkap.</p>
            </div>
            <WhatsAppButton message={heroMessage} label="Minta quotation masker COMET" placement="final_cta" className="inline-flex flex-shrink-0 items-center justify-center gap-2.5 rounded-xl bg-[#08243a] px-7 py-4 text-base font-black text-white transition-colors hover:bg-slate-800" />
          </div>
        </section>

        <BrandTrustSections brandName="COMET" itemName="masker COMET" images={proofImages} questions={COMET_MASK_TRUST_QUESTIONS} />
        <LpCrossLinks currentPath={PAGE_PATH} />
      </main>
      <Footer config={config} categories={categories} />
    </div>
  )
}
